// Package imports:
import React, { useMemo } from 'react';
import cx from 'classnames';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import Link from '../../ui-elements/Link/Link';
import ColoredNumber from '../../ui-elements/ColoredNumber/ColoredNumber';
import DelayOrClosedLabel from '../DelayOrClosedLabel/DelayOrClosedLabel';
import DatePicker from '../DatePicker/DatePicker';
import PriceRange from '../../ui-elements/PriceRange/PriceRange';
import LmdTable from '../../ui-elements/Table/LmdTable';
import LmdSingleLineTable from '../../ui-elements/Table/LmdSingleLineTable';
// Service imports:
import { convertToPercentage, formatNumber, displayQuantityShorthand, getAPItoday, getTableDate } from '../../services/utils';
import { useApiLmdDataMappedByString, useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IApiLmdSnapshot, IApiLmdStatistics, IApiLmdHistory1 } from '../../types/HlutabrefTypes';
import { ApiLmdData, IDefaultProps } from '../../types/Types';

interface IOwnProps {
    symbol?: string,
    exchange?: string,
    market?: string,
    showDatepicker?: boolean
}

interface ICombinedData {
    symbol: string | null,
    last_price: number | null,
    bid: number | null,
    ask: number | null,
    intraday_accumulated_turnover: number | null,
    intraday_no_of_trades: number | null,
    last_price_diff: number | null,
    weeks4_price_change_per?: number | null,
    year_price_change_per?: number | null,
    months12_price_change_per?: number | null,
    last_trade_dateutc?: string | null,
    months12_high_price?: number | null,
    months12_low_price?: number | null
}

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const SharesTable: React.FC<Props> = ({
    symbol,
    exchange = '*',
    market = '*',
    showDatepicker,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [data] = useApiLmdData<IApiLmdSnapshot[]>(
        (symbol
            ? `/v1/market_data/v1/exchanges/${exchange}/markets/${market}/equities/${symbol}/delayed_snapshot`
            : '/v1/market_data/v1/keldan/exchanges/[XICE;FNIS]/equities/delayed_snapshot'
        ),
        accessToken,
        refreshRateMs
    );
    const [currentDate, setCurrentDate, statistics] = useApiLmdDataMappedByString<IApiLmdStatistics[]>(
        getAPItoday(),
        (date: string) => (
            symbol
                ? `/v1/market_data/v1/exchanges/${exchange}/markets/${market}/tradables/${symbol}/statistics_delayed?trading_date=${date}`
                :`/v1/market_data/v1/keldan/exchanges/[XICE;FNIS]/statistics_delayed?security_type=share&trading_date=${date}`
        ),
        accessToken,
        refreshRateMs
    );
    const [, setHistoryDate, history] = useApiLmdDataMappedByString<IApiLmdHistory1[]>(
        getAPItoday(),
        (date: string) => `/v1/market_data/v1/keldan/exchanges/[XICE;FNIS]/closingprices?from_date=${date}&to_date=${date}`,
        accessToken
    )

    const isToday = useMemo(() => currentDate === getAPItoday(), [currentDate]);

    const displayData = useMemo(() => {
        const newData: ICombinedData[] = [];
        if (isToday) {
            if (data.data === null || data.error instanceof Error || statistics.data === undefined || statistics.error instanceof Error) {
                return newData;
            }
            for (let entry of data.data) {
                const statsEntry = statistics.data?.find(e => e.symbol === entry.symbol);
                newData.push({
                    symbol: entry.symbol,
                    last_price: entry.last_price,
                    bid: entry.bid,
                    ask: entry.ask,
                    intraday_accumulated_turnover: entry.intraday_accumulated_turnover,
                    intraday_no_of_trades: entry.intraday_no_of_trades,
                    last_price_diff: entry.intraday_per_change,
                    weeks4_price_change_per: statsEntry?.weeks4_price_change_per,
                    year_price_change_per: statsEntry?.year_price_change_per,
                    months12_price_change_per: statsEntry?.months12_price_change_per,
                    last_trade_dateutc: entry.last_trade_dateutc,
                    months12_high_price: statsEntry?.months12_high_price,
                    months12_low_price: statsEntry?.months12_low_price
                });
            }
        } else {
            const historyForDate = history.data?.filter(x => !x.MarketName?.includes('Bond'));
            if (historyForDate === undefined || historyForDate instanceof Error || historyForDate === null || statistics?.data === undefined || statistics.error instanceof Error) {
                return newData;
            }
            for (let entry of historyForDate) {
                const statsEntry = statistics.data?.find(e => e.symbol === entry.Symbol);
                newData.push({
                    symbol: entry.Symbol,
                    last_price: entry.Close,
                    bid: null,
                    ask: null,
                    intraday_accumulated_turnover: entry.Volume,
                    intraday_no_of_trades: entry.NumberOfTrades,
                    last_price_diff: null,
                    weeks4_price_change_per: statsEntry?.weeks4_price_change_per,
                    year_price_change_per: statsEntry?.year_price_change_per,
                    months12_price_change_per: statsEntry?.months12_price_change_per,
                    last_trade_dateutc: entry.TradingDate,
                    months12_high_price: statsEntry?.months12_high_price,
                    months12_low_price: statsEntry?.months12_low_price
                });
            }
        }
        return newData.sort((a, b) => {
            return a.symbol === null && b.symbol === null
                ? 0
                : a.symbol === null
                ? -1
                : b.symbol === null
                ? 1
                : a.symbol > b.symbol
                ? 1
                : -1;
        });
    }, [data, statistics, history]);

    const apiData: ApiLmdData<ICombinedData[]> = {
        data: displayData,
        error: data.error ?? statistics.error ?? history.error ?? null
    }
    
    return (
        <DisplayBox
            className={cx('KCL_shares-table', {'hide-symbol': symbol})}
            title={symbol ?? "Hlutabréf"}
            statusComponent={<DelayOrClosedLabel closeType='shares' />}
            asideComponent={showDatepicker
                ? <DatePicker
                    maxDate={new Date()}
                    size="lg"
                    selectedDate={new Date(currentDate)}
                    setSelectedDate={(newDate) => { setCurrentDate(getAPItoday(newDate)); setHistoryDate(getAPItoday(newDate)); }}
                />
                : undefined
            }
            {...props}
        >
            {symbol
                ? <LmdSingleLineTable
                    apiData={apiData}
                    columns={[{
                        title: 'Félag',
                        dataAccessor: ({symbol}) => <Link url={`/Markadir/Hlutabref/${symbol}`} linkSize='15'>{symbol}</Link>,
                        textAlign: 'left',
                    }, {
                        title: 'Verð',
                        dataAccessor: ({last_price}) => formatNumber(last_price),
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: 'Kaup',
                        dataAccessor: ({bid}) => formatNumber(bid),
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: 'Sala',
                        dataAccessor: ({ask}) => formatNumber(ask),
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: 'Velta',
                        dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: 'Fj. viðsk.',
                        dataAccessor: ({intraday_no_of_trades}) => intraday_no_of_trades,
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: 'Dagsbreyting',
                        dataAccessor: ({last_price_diff}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={last_price_diff !== null}
                                    sign={!last_price_diff ? 'neutral' : last_price_diff > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(last_price_diff, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: '1 mán.',
                        dataAccessor: ({weeks4_price_change_per}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={weeks4_price_change_per !== null}
                                    sign={!weeks4_price_change_per ? 'neutral' : weeks4_price_change_per > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(weeks4_price_change_per ?? null, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: 'Innan árs',
                        dataAccessor: ({year_price_change_per}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={year_price_change_per !== null}
                                    sign={!year_price_change_per ? 'neutral' : year_price_change_per > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(year_price_change_per ?? null, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: '1 ár',
                        dataAccessor: ({months12_price_change_per}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={months12_price_change_per !== null}
                                    sign={!months12_price_change_per ? 'neutral' : months12_price_change_per > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(months12_price_change_per ?? null, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: symbol ? 'left' : 'right',
                    }, {
                        title: 'Síð. viðsk.',
                        dataAccessor: ({last_trade_dateutc}) => getTableDate(last_trade_dateutc ?? null, 'HH:MM:SS', '.'),
                        textAlign: symbol ? 'left' : 'right'
                    }, {
                        title: 'Árs bil',
                        dataAccessor: ({months12_low_price, months12_high_price, last_price}) => (
                            <PriceRange low={months12_low_price ?? 0} high={months12_high_price ?? 0} value={last_price ?? 0} />
                        ),
                    }]}
                    tableSize="lg"
                />
                : <LmdTable
                    apiData={apiData}
                    columns={[{
                        title: 'Félag',
                        dataAccessor: ({symbol}) => <Link url={`/Markadir/Hlutabref/${symbol}`} linkSize='15'>{symbol}</Link>,
                        textAlign: 'left',
                        simpleSortable: ({symbol}) => symbol
                    }, {
                        title: 'Verð',
                        dataAccessor: ({last_price}) => formatNumber(last_price),
                        textAlign: 'right',
                        simpleSortable: ({last_price}) => last_price
                    }, {
                        title: 'Kaup',
                        dataAccessor: ({bid}) => formatNumber(bid),
                        textAlign: 'right',
                        simpleSortable: ({bid}) => bid
                    }, {
                        title: 'Sala',
                        dataAccessor: ({ask}) => formatNumber(ask),
                        textAlign: 'right',
                        simpleSortable: ({ask}) => ask
                    }, {
                        title: 'Velta',
                        dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                        textAlign: 'right',
                        simpleSortable: ({intraday_accumulated_turnover}) => intraday_accumulated_turnover
                    }, {
                        title: 'Fj. viðsk.',
                        dataAccessor: ({intraday_no_of_trades}) => intraday_no_of_trades,
                        textAlign: 'right',
                        simpleSortable: ({intraday_no_of_trades}) => intraday_no_of_trades
                    }, {
                        title: 'Dagsbreyting',
                        dataAccessor: ({last_price_diff}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={last_price_diff !== null}
                                    sign={!last_price_diff ? 'neutral' : last_price_diff > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(last_price_diff, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'right',
                        simpleSortable: ({last_price_diff}) => last_price_diff
                    }, {
                        title: '1 mán.',
                        dataAccessor: ({weeks4_price_change_per}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={weeks4_price_change_per !== null}
                                    sign={!weeks4_price_change_per ? 'neutral' : weeks4_price_change_per > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(weeks4_price_change_per ?? null, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'right',
                        simpleSortable: ({weeks4_price_change_per}) => weeks4_price_change_per ?? null
                    }, {
                        title: 'Innan árs',
                        dataAccessor: ({year_price_change_per}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={year_price_change_per !== null}
                                    sign={!year_price_change_per ? 'neutral' : year_price_change_per > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(year_price_change_per ?? null, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'right',
                        simpleSortable: ({year_price_change_per}) => year_price_change_per ?? null
                    }, {
                        title: '1 ár',
                        dataAccessor: ({months12_price_change_per}) => {
                            return (
                                <ColoredNumber
                                    hasArrow={months12_price_change_per !== null}
                                    sign={!months12_price_change_per ? 'neutral' : months12_price_change_per > 0 ? 'pos' : 'neg'}
                                >
                                    {convertToPercentage(months12_price_change_per ?? null, true)}
                                </ColoredNumber>
                            )
                        },
                        textAlign: 'right',
                        simpleSortable: ({months12_price_change_per}) => months12_price_change_per ?? null
                    }, {
                        title: 'Síð. viðsk.',
                        dataAccessor: ({last_trade_dateutc}) => getTableDate(last_trade_dateutc ?? null, 'HH:MM:SS', '.'),
                        textAlign: 'right'
                    }, {
                        title: 'Árs bil',
                        dataAccessor: ({months12_low_price, months12_high_price, last_price}) => (
                            <PriceRange low={months12_low_price ?? 0} high={months12_high_price ?? 0} value={last_price ?? 0} />
                        ),
                        simpleSortable: ({months12_low_price, last_price, months12_high_price}) => ((last_price ?? 0) - (months12_low_price ?? 0)) / (((months12_high_price ?? 0) - (months12_low_price ?? 0)) ?? 1)
                    }]}
                    tableSize="lg"
                />
            }
        </DisplayBox>
    );
}

export default SharesTable;